import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Sparkles, Camera, Info, MapPin, LocateFixed, Building2, X } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
  useIsDesktop,
} from "@/components/ui/responsive-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { AiSuggestionCard } from "@/components/AiSuggestionCard";
import { AddressAutocomplete } from "@/components/geo/AddressAutocomplete";
import { fileToDataUrl } from "@/components/PhotoUploader";
import { useAiStatus } from "@/hooks/useAiStatus";
import { CATEGORY_LABEL } from "@/lib/format";
import { getCurrentPosition, reverseGeocode, LOCATION_SOURCE_LABEL } from "@/lib/geo";
import { useFeatures } from "@/context/AuthContext";
import api from "@/lib/api";

const EMPTY = {
  customer_id: "",
  brand: "",
  type: "",
  category: "",
  serial_number: "",
  internal_number: "",
  year: "",
  current_hours: "",
  service_interval_hours: "",
  service_interval_months: "",
  last_service_date: "",
  next_service_date: "",
  control_last_date: "",
  control_interval_months: "12",
  engine: "",
  weight_kg: "",
  oil_type: "",
  oil_capacity_l: "",
  hydraulic_oil_type: "",
  coolant_type: "",
  notes: "",
};

const numFields = ["year", "current_hours", "service_interval_hours", "service_interval_months", "control_interval_months", "weight_kg", "oil_capacity_l"];

const Section = ({ title, hint }) => (
  <div className="col-span-full mt-2 flex items-baseline justify-between border-b border-border pb-1.5">
    <span className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</span>
    {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
  </div>
);

const Field = ({ label, children, className = "", hint }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-sm font-semibold">
      {label} {hint && <span className="font-normal text-muted-foreground">– {hint}</span>}
    </Label>
    {children}
  </div>
);

export const MachineDialog = ({ open, onOpenChange, machine, defaultCustomerId, onSaved }) => {
  const ai = useAiStatus();
  const desktop = useIsDesktop();
  const { map_enabled } = useFeatures();
  const [form, setForm] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const isEdit = !!machine;

  // AI state
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [addParts, setAddParts] = useState(true);
  const [plateBusy, setPlateBusy] = useState(false);
  const [plate, setPlate] = useState(null);
  const plateInputRef = useRef(null);

  // Plassering (valfri overstyring av kundens adresse)
  const [location, setLocation] = useState(null); // {address, lat, lng, source, note}
  const [locationCleared, setLocationCleared] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    setLookup(null);
    setPlate(null);
    setLocationCleared(false);
    setLocation(machine?.location?.lat != null ? machine.location : null);
    if (machine) {
      const next = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => {
        if (machine[k] !== null && machine[k] !== undefined) next[k] = String(machine[k]);
      });
      next.customer_id = machine.customer_id || "";
      setForm(next);
    } else {
      setForm({ ...EMPTY, customer_id: defaultCustomerId || "" });
    }
  }, [open, machine, defaultCustomerId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v ?? "" }));

  // ---------- AI: maskin-oppslag ----------
  const runLookup = async () => {
    if (!form.brand.trim()) return toast.info("Skriv inn maskinmerke (og gjerne modell) først.");
    setLookupBusy(true);
    try {
      const r = await api.post("/ai/machine-lookup", { brand: form.brand, model: form.type, extra: form.year ? `Årsmodell ${form.year}` : null });
      setLookup(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI-oppslag feila. Fyll inn manuelt.");
    } finally {
      setLookupBusy(false);
    }
  };

  const lookupItems = useMemo(() => {
    if (!lookup) return [];
    const f = (key, label, value, display) => ({ key, label, value, display, defaultChecked: value !== null && value !== undefined && value !== "" && !form[key] });
    return [
      f("category", "Kategori", lookup.category),
      f("engine", "Motor", lookup.engine),
      f("weight_kg", "Vekt (kg)", lookup.weight_kg, lookup.weight_kg ? `${Number(lookup.weight_kg).toLocaleString("nb-NO")} kg` : null),
      f("oil_type", "Motorolje", lookup.oil_type),
      f("oil_capacity_l", "Oljemengd (l)", lookup.oil_capacity_l, lookup.oil_capacity_l ? `${lookup.oil_capacity_l} l` : null),
      f("hydraulic_oil_type", "Hydraulikkolje", lookup.hydraulic_oil_type),
      f("coolant_type", "Kjølevæske", lookup.coolant_type),
      f("service_interval_hours", "Serviceintervall (t)", lookup.service_interval_hours, lookup.service_interval_hours ? `${lookup.service_interval_hours} t` : null),
      f("service_interval_months", "Serviceintervall (md)", lookup.service_interval_months, lookup.service_interval_months ? `${lookup.service_interval_months} md` : null),
    ];
  }, [lookup, form]);

  const pendingParts = useMemo(() => {
    if (!lookup) return [];
    const parts = [];
    (lookup.filters || []).forEach((p) => p?.name && parts.push({ name: p.name, category: "filter", part_number: p.part_number || "", quantity: 1, unit: "stk" }));
    (lookup.fluids || []).forEach((p) => p?.name && parts.push({ name: p.name, category: p.category === "kjolevaeske" ? "kjolevaeske" : "olje", quantity: p.quantity || 1, unit: p.unit || "liter" }));
    return parts;
  }, [lookup]);

  const applyLookup = (keys) => {
    setForm((f) => {
      const n = { ...f };
      keys.forEach((k) => {
        const v = lookup[k];
        if (v !== null && v !== undefined && v !== "") n[k] = String(v);
      });
      if (lookup.notes && !n.notes) n.notes = lookup.notes;
      return n;
    });
    setLookup((l) => ({ ...l, applied: true }));
    toast.success(`${keys.length} felt fylt ut frå AI-forslag. Kontroller verdiane.`);
  };

  // ---------- AI: les typeskilt ----------
  const onPlateFile = async (file) => {
    if (!file) return;
    setPlateBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file, 1600, 0.9);
      const r = await api.post("/ai/read-type-plate", { image: dataUrl });
      if (r.data?.is_type_plate === false) toast.warning("Biletet ser ikkje ut som eit typeskilt – sjekk forslaga ekstra godt.");
      setPlate(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje lese typeskiltet. Fyll inn manuelt.");
    } finally {
      setPlateBusy(false);
      if (plateInputRef.current) plateInputRef.current.value = "";
    }
  };

  const plateItems = useMemo(() => {
    if (!plate) return [];
    const c = plate.confidence || {};
    return [
      { key: "brand", label: "Maskinmerke", value: plate.brand, confidence: c.brand },
      { key: "type", label: "Type / modell", value: plate.model, confidence: c.model },
      { key: "serial_number", label: "Serienummer", value: plate.serial_number, confidence: c.serial_number },
      { key: "year", label: "Årsmodell", value: plate.year, confidence: c.year },
      { key: "engine", label: "Motor", value: plate.engine },
      { key: "weight_kg", label: "Vekt (kg)", value: plate.weight_kg },
    ];
  }, [plate]);

  const applyPlate = (keys) => {
    const map = { brand: plate.brand, type: plate.model, serial_number: plate.serial_number, year: plate.year, engine: plate.engine, weight_kg: plate.weight_kg };
    setForm((f) => {
      const n = { ...f };
      keys.forEach((k) => {
        if (map[k] !== null && map[k] !== undefined && map[k] !== "") n[k] = String(map[k]);
      });
      return n;
    });
    setPlate(null);
    toast.success("Felt fylt ut frå typeskiltet. Kontroller serienummeret.");
  };

  // ---------- Plassering ----------
  const selectedCustomer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);

  const useMyPosition = async () => {
    setGpsBusy(true);
    const pos = await getCurrentPosition();
    if (!pos) {
      setGpsBusy(false);
      return toast.error("Fekk ikkje posisjon. Gi nettlesaren tilgang til posisjon og prøv igjen.");
    }
    const hit = await reverseGeocode(pos.lat, pos.lng);
    setGpsBusy(false);
    setLocation({ address: hit?.address || `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`, lat: pos.lat, lng: pos.lng, source: "gps", accuracy_m: pos.accuracy_m, note: location?.note || "" });
    setLocationCleared(false);
    toast.success(hit?.address ? `Plassering sett til ${hit.address}` : "Plassering sett frå GPS.");
  };

  const clearLocation = () => {
    setLocation(null);
    setLocationCleared(true);
  };

  // ---------- Save ----------
  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Vel ein kunde.");
    if (!form.brand.trim()) return toast.error("Maskinmerke er påkrevd.");
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v === "" || v === null) return;
      payload[k] = numFields.includes(k) ? Number(v) : v;
    });
    if (location?.lat != null && location?.lng != null) {
      payload.location = { address: location.address || null, lat: location.lat, lng: location.lng, source: location.source || "manual", note: location.note || null, accuracy_m: location.accuracy_m ?? null };
    }
    setBusy(true);
    try {
      let saved;
      if (isEdit) saved = (await api.put(`/machines/${machine.id}`, payload)).data;
      else saved = (await api.post("/machines", payload)).data;
      if (isEdit && locationCleared && machine?.location) {
        try {
          saved = (await api.delete(`/machines/${machine.id}/location`)).data;
        } catch (err) {
          /* ikkje kritisk */
        }
      }

      if (lookup && addParts && pendingParts.length > 0) {
        let ok = 0;
        for (const p of pendingParts) {
          try {
            await api.post("/parts", { machine_id: saved.id, ...p, notes: "AI-forslag – kontroller delenr" });
            ok += 1;
          } catch (err) {
            /* skip */
          }
        }
        if (ok) toast.success(`${ok} delar lagt inn på maskina (merkte som AI-forslag).`);
      }
      toast.success(isEdit ? "Maskin oppdatert." : "Maskin oppretta.");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Noko gjekk gale.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[94vh] flex-col gap-0 p-0 sm:max-w-2xl" drawerClassName="max-h-[94dvh]" data-testid="machine-dialog">
        <DialogHeader className={desktop ? "border-b px-6 pb-4 pt-6" : undefined}>
          <DialogTitle className="font-heading text-xl font-bold">{isEdit ? "Rediger maskin" : "Ny maskin"}</DialogTitle>
          <DialogDescription>Registrer maskina og knyt henne til kunden. Alle felt unntatt kunde og merke er valfrie.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="contents" data-testid="machine-form">
          <DialogBody className={desktop ? "min-h-0 flex-1 grid-cols-1 content-start overflow-y-auto px-6 py-4 sm:grid-cols-2" : "sm:grid-cols-2"}>
          <Field label="Kunde *" className="sm:col-span-2">
            <Select value={form.customer_id} onValueChange={(v) => setVal("customer_id", v)}>
              <SelectTrigger className="h-11" data-testid="machine-customer-select"><SelectValue placeholder="Vel kunde" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.department ? ` – ${c.department}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Section title="Identifikasjon" />
          <Field label="Maskinmerke *">
            <Input className="h-11" data-testid="machine-brand-input" value={form.brand} onChange={set("brand")} placeholder="CAT, Volvo, Komatsu …" />
          </Field>
          <Field label="Type / modell">
            <Input className="h-11" data-testid="machine-type-input" value={form.type} onChange={set("type")} placeholder="EC220E, M315 …" />
          </Field>

          {/* ---- AI assist bar (optional) ---- */}
          {ai.enabled && (
            <div className="sm:col-span-2" data-testid="machine-ai-bar">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-3 py-2.5">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="mr-auto text-xs text-muted-foreground">Valfri hjelp – du kan fylle ut alt manuelt.</span>
                <Button type="button" size="sm" variant="outline" onClick={runLookup} disabled={lookupBusy || !form.brand.trim()} data-testid="ai-machine-lookup-button">
                  {lookupBusy ? <Loader2 className="animate-spin" /> : <Sparkles className="text-primary" />} {lookupBusy ? "Hentar …" : "Hent info med AI"}
                </Button>
                <input ref={plateInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="ai-type-plate-input" onChange={(e) => onPlateFile(e.target.files?.[0])} />
                <Button type="button" size="sm" variant="outline" onClick={() => plateInputRef.current?.click()} disabled={plateBusy} data-testid="ai-type-plate-button">
                  {plateBusy ? <Loader2 className="animate-spin" /> : <Camera />} {plateBusy ? "Les …" : "Les typeskilt"}
                </Button>
              </div>
            </div>
          )}

          {plate && (
            <div className="sm:col-span-2">
              <AiSuggestionCard
                testid="ai-plate-card"
                title="Lese frå typeskilt – kontroller"
                subtitle={plate.disclaimer}
                items={plateItems}
                onApply={applyPlate}
                onIgnore={() => setPlate(null)}
              >
                {plate.raw_text && (
                  <details className="mt-3 text-xs text-muted-foreground">
                    <summary className="cursor-pointer font-semibold">Vis all tekst AI las på skiltet</summary>
                    <pre className="mt-1 whitespace-pre-wrap font-body">{plate.raw_text}</pre>
                  </details>
                )}
              </AiSuggestionCard>
            </div>
          )}

          {lookup && !lookup.applied && (
            <div className="sm:col-span-2">
              <AiSuggestionCard
                testid="ai-lookup-card"
                title={`Forslag for ${lookup.label}`}
                subtitle={`${lookup.disclaimer} Tryggleik: ${lookup.confidence || "middels"}.`}
                items={lookupItems}
                onApply={applyLookup}
                onIgnore={() => setLookup(null)}
              >
                {pendingParts.length > 0 && (
                  <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border bg-card p-2.5">
                    <Checkbox checked={addParts} onCheckedChange={(v) => setAddParts(!!v)} data-testid="ai-lookup-add-parts" className="mt-0.5" />
                    <span className="text-sm">
                      <span className="font-semibold">Legg inn {pendingParts.length} delar på maskina ved lagring</span>
                      <span className="block text-xs text-muted-foreground">
                        {pendingParts.slice(0, 5).map((p) => `${p.name} (${CATEGORY_LABEL[p.category]})`).join(", ")}{pendingParts.length > 5 ? " …" : ""}
                      </span>
                    </span>
                  </label>
                )}
                {lookup.notes && (
                  <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {lookup.notes}</p>
                )}
              </AiSuggestionCard>
            </div>
          )}

          <Field label="Serie-/chassisnummer">
            <Input className="h-11" data-testid="machine-serial-input" value={form.serial_number} onChange={set("serial_number")} />
          </Field>
          <Field label="Kundens maskinnr" hint="t.d. 5380">
            <Input className="h-11" data-testid="machine-internal-input" value={form.internal_number} onChange={set("internal_number")} />
          </Field>
          <Field label="Årsmodell">
            <Input className="h-11" type="number" inputMode="numeric" data-testid="machine-year-input" value={form.year} onChange={set("year")} />
          </Field>
          <Field label="Kategori" hint="beltegravar, hjullastar …">
            <Input className="h-11" data-testid="machine-category-input" value={form.category} onChange={set("category")} />
          </Field>
          <Field label="Timetal nå (t)" hint="kun informasjon">
            <Input className="h-11" type="number" inputMode="decimal" data-testid="machine-hours-input" value={form.current_hours} onChange={set("current_hours")} />
          </Field>

          <Section title="Service" hint="datobasert oppfølging" />
          <Field label="Serviceintervall (md)">
            <Input className="h-11" type="number" inputMode="numeric" data-testid="machine-interval-months-input" value={form.service_interval_months} onChange={set("service_interval_months")} placeholder="t.d. 12" />
          </Field>
          <Field label="Serviceintervall (t)" hint="referanse">
            <Input className="h-11" type="number" inputMode="numeric" value={form.service_interval_hours} onChange={set("service_interval_hours")} placeholder="t.d. 500" />
          </Field>
          <Field label="Dato siste service">
            <DatePicker value={form.last_service_date} onChange={(v) => setVal("last_service_date", v)} testid="machine-last-service-date" />
          </Field>
          <Field label="Planlagt neste service" hint="overstyrer intervall">
            <DatePicker value={form.next_service_date} onChange={(v) => setVal("next_service_date", v)} testid="machine-next-service-date" />
          </Field>

          <Section title="Årskontroll" />
          <Field label="Dato siste årskontroll">
            <DatePicker value={form.control_last_date} onChange={(v) => setVal("control_last_date", v)} testid="machine-control-date-input" />
          </Field>
          <Field label="Intervall (md)">
            <Input className="h-11" type="number" inputMode="numeric" value={form.control_interval_months} onChange={set("control_interval_months")} />
          </Field>

          <Section title="Spesifikasjonar" hint="olje, filter, kjølevæske" />
          <Field label="Motor">
            <Input className="h-11" data-testid="machine-engine-input" value={form.engine} onChange={set("engine")} placeholder="Volvo D6J, 129 kW" />
          </Field>
          <Field label="Driftsvekt (kg)">
            <Input className="h-11" type="number" inputMode="numeric" value={form.weight_kg} onChange={set("weight_kg")} />
          </Field>
          <Field label="Motorolje">
            <Input className="h-11" data-testid="machine-oil-input" value={form.oil_type} onChange={set("oil_type")} placeholder="15W-40 API CJ-4" />
          </Field>
          <Field label="Oljemengd (l)">
            <Input className="h-11" type="number" inputMode="decimal" value={form.oil_capacity_l} onChange={set("oil_capacity_l")} />
          </Field>
          <Field label="Hydraulikkolje">
            <Input className="h-11" value={form.hydraulic_oil_type} onChange={set("hydraulic_oil_type")} placeholder="ISO VG 46 HVLP" />
          </Field>
          <Field label="Kjølevæske">
            <Input className="h-11" data-testid="machine-coolant-input" value={form.coolant_type} onChange={set("coolant_type")} placeholder="OAT etylenglykol" />
          </Field>

          {map_enabled && (<>
          <Section title="Plassering" hint="valfritt – elles kundens adresse" />
          <div className="sm:col-span-2 space-y-2.5" data-testid="machine-location-section">
            {location?.lat != null ? (
              <div className="flex items-start gap-2.5 rounded-lg border bg-card p-2.5" data-testid="machine-location-current">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/12 text-[#9a2f00] dark:text-primary" style={{ backgroundColor: "rgba(255,78,0,0.12)" }}>
                  <MapPin className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 text-sm">
                  <div className="truncate font-semibold">{location.address || `${Number(location.lat).toFixed(5)}, ${Number(location.lng).toFixed(5)}`}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    Overstyrt plassering · {LOCATION_SOURCE_LABEL[location.source] || "Manuelt"}{location.updated_at ? ` · ${new Date(location.updated_at).toLocaleDateString("nb-NO")}` : ""}
                  </div>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={clearLocation} data-testid="machine-location-clear" className="shrink-0 text-muted-foreground">
                  <X /> <span className="hidden sm:inline">Bruk kundens adresse</span><span className="sm:hidden">Fjern</span>
                </Button>
              </div>
            ) : (
              <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="machine-location-default">
                <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {selectedCustomer?.address
                  ? <>Maskina blir vist på kundens adresse: <span className="font-medium text-foreground">{selectedCustomer.address}</span>{selectedCustomer.lat == null && " (adressa har ikkje koordinatar enno)"}</>
                  : selectedCustomer
                    ? "Kunden har inga adresse – legg inn anleggsadresse her, eller på kunden, for å få maskina på kartet."
                    : "Vel kunde først. Legg inn eiga adresse berre om maskina står ein annan stad enn hjå kunden."}
              </p>
            )}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
              <AddressAutocomplete
                value={{ address: location?.address || "", lat: location?.lat ?? null, lng: location?.lng ?? null }}
                onChange={(a) => {
                  if (!a.address) return clearLocation();
                  if (a.lat != null) {
                    setLocation({ address: a.address, lat: a.lat, lng: a.lng, source: "manual", note: location?.note || "" });
                    setLocationCleared(false);
                  }
                }}
                placeholder="Anleggs-/prosjektadresse …"
                testid="machine-location-address"
              />
              <Button type="button" variant="outline" className="h-11" onClick={useMyPosition} disabled={gpsBusy} data-testid="machine-location-gps">
                {gpsBusy ? <Loader2 className="animate-spin" /> : <LocateFixed />} Bruk min posisjon
              </Button>
            </div>
            {location?.lat != null && (
              <Input className="h-11" placeholder="Notat om plassering (t.d. «Prosjekt E16, brakkerigg»)" value={location.note || ""} onChange={(e) => setLocation((l) => ({ ...l, note: e.target.value }))} data-testid="machine-location-note" />
            )}
          </div>
          </>)}

          <Field label="Notat" className="sm:col-span-2">
            <Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="machine-notes-input" />
          </Field>

          </DialogBody>
          <DialogFooter className={desktop ? "gap-2 border-t px-6 py-3 sm:gap-0" : undefined}>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="machine-cancel-button">Avbryt</Button>
            <Button type="submit" disabled={busy} data-testid="save-machine-button">
              {busy && <Loader2 className="animate-spin" />}
              {isEdit ? "Lagre endringar" : "Opprett maskin"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
